import { z } from 'zod';
import { TravelerTravelPreferencesSchema } from '../traveler/travel_preferences.js';
import {
  ModalSessionCorrelationSchema,
  ModalSessionItineraryLineageSchema,
} from './correlation.js';
import { ModalSessionBookingWriteAheadSchema } from './booking_write_ahead.js';
import {
  ModalSessionBookingRequesterSchema,
  ModalSessionCorporateSelectionSchema,
} from './booking_request_info.js';
import { ModalSessionFlightPolicyRuleSchema } from './flight_policy_violation.js';

const forbiddenPublicKeys = new Set([
  'adapterState',
  'searchKey',
  'departingKey',
  'returningKey',
  'flightId',
  'hotelId',
  'hotelToken',
  'correlationId',
  'roomId',
  'mediaRoomId',
  'supplierId',
  'bookingToken',
  'roomOption',
  'roomNumber',
]);

function containsForbiddenPublicKey(value: unknown): boolean {
  if (Array.isArray(value)) return value.some(containsForbiddenPublicKey);
  if (!value || typeof value !== 'object') {
    return false;
  }
  return Object.entries(value).some(
    ([key, nested]) => forbiddenPublicKeys.has(key) || containsForbiddenPublicKey(nested),
  );
}

const ModalPayloadSchema = z
  .record(z.string(), z.unknown())
  .refine((value) => !containsForbiddenPublicKey(value), {
    message: 'Public modal payload contains a private key',
  });

export const ModalSessionIdSchema = z.string().min(1);
export type ModalSessionId = z.infer<typeof ModalSessionIdSchema>;

export const ModalOfferIdSchema = z.string().min(1);
export type ModalOfferId = z.infer<typeof ModalOfferIdSchema>;

export const ModalPricingOptionIdSchema = z.string().min(1);
export type ModalPricingOptionId = z.infer<typeof ModalPricingOptionIdSchema>;

export const ModalQuoteIdSchema = z.string().min(1);
export type ModalQuoteId = z.infer<typeof ModalQuoteIdSchema>;

export const ModalSessionModalSchema = z.enum(['flight', 'hotel', 'rental', 'mobility']);
export type ModalSessionModal = z.infer<typeof ModalSessionModalSchema>;

export const ModalSessionStatusSchema = z.enum([
  'created',
  'searching',
  'offers_available',
  'offer_selected',
  'pricing_available',
  'quoted',
  'checkout_ready',
  'booking_requested',
  'pending_approval',
  'confirmed',
  'failed',
  'expired',
]);
export type ModalSessionStatus = z.infer<typeof ModalSessionStatusSchema>;

export const ModalSessionOwnerSchema = z
  .object({
    orgId: z.string().min(1),
    userId: z.string().min(1).optional(),
    conversationId: z.string().min(1).optional(),
  })
  .strict();
export type ModalSessionOwner = z.infer<typeof ModalSessionOwnerSchema>;

export const ModalSessionMoneySchema = z
  .object({
    amount: z.number().nonnegative(),
    currency: z.string().min(3).max(3),
  })
  .strict();
export type ModalSessionMoney = z.infer<typeof ModalSessionMoneySchema>;

export const ModalSessionFlightPolicySchema = z
  .object({
    status: z.enum(['in', 'out']),
    rules: z.array(ModalSessionFlightPolicyRuleSchema).optional(),
    /** Teto de preço DECLARADO pela política aplicada, com moeda — o único número
     *  que a lista pode anunciar como limite da empresa. OPCIONAL porque a
     *  definição de política do viajante ainda não expõe o cap por rota/classe
     *  (#2457): sem o dado a UI não estampa número nenhum, em vez de inferir um
     *  da página carregada. A moeda viaja junto: cap em USD ao lado de preço em
     *  BRL não se compara sem conversão explícita. */
    ceiling: ModalSessionMoneySchema.optional(),
  })
  .strict();
export type ModalSessionFlightPolicy = z.infer<typeof ModalSessionFlightPolicySchema>;

const ModalSessionFlightConnectionViewSchema = z
  .object({
    iata: z.string().min(1),
    layoverLabel: z.string().min(1).optional(),
  })
  .strict();

/** Identidade de UMA perna (voo físico) de um trecho com conexão: a chave que o
 *  enriquecimento Cirium por perna (agents) precisa — cia + número + pontas + data.
 *  `departAt` é a hora LOCAL da partida com `Z` espúrio (`completeDate` do gateway);
 *  a data local é `slice(0, 10)`. NÃO exportado: só o tipo viaja (padrão de
 *  `ModalSessionFlightConnectionViewSchema`). */
const ModalSessionFlightLegRefSchema = z
  .object({
    carrier: z.string().min(1),
    flightNumber: z.string().min(1),
    from: z.string().min(1),
    to: z.string().min(1),
    departAt: z.string().min(1),
  })
  .strict();
export type ModalSessionFlightLegRef = z.infer<typeof ModalSessionFlightLegRefSchema>;

export const ModalSessionFlightSegmentViewSchema = z
  .object({
    from: z.string().min(1),
    to: z.string().min(1),
    departAt: z.string().min(1),
    arriveAt: z.string().min(1),
    carrier: z.string().min(1),
    flightNumber: z.string().min(1),
    durationLabel: z.string().optional(),
    carrierName: z.string().optional(),
    durationMinutes: z.number().int().positive().optional(),
    connections: z.array(ModalSessionFlightConnectionViewSchema).optional(),
  })
  .strict();
export type ModalSessionFlightSegmentView = z.infer<typeof ModalSessionFlightSegmentViewSchema>;

/**
 * Sujeito EFETIVO da política da busca que produziu a oferta: o viajante cuja
 * conformidade governou o veredito within-policy assado nela. `explicit` = o
 * `policyTravelerId` do pedido (id de roster); `requester` = o fallback para a
 * identidade que fez a busca (handle do canal).
 *
 * A ORIGEM viaja junto porque os dois ramos produzem `string` sem namespace: quem
 * compara na adoção precisa saber contra qual lado casar, e cruzar os espaços
 * recusaria usuário corporativo legítimo em massa.
 */
export const ModalSessionFlightPolicySubjectSchema = z
  .object({
    id: z.string().min(1),
    source: z.enum(['explicit', 'requester']),
  })
  .strict();
export type ModalSessionFlightPolicySubject = z.infer<typeof ModalSessionFlightPolicySubjectSchema>;

export const ModalSessionFlightOfferViewSchema = z
  .object({
    offerId: ModalOfferIdSchema,
    carrier: z.string().min(1),
    departAt: z.string().min(1),
    arriveAt: z.string().min(1),
    price: ModalSessionMoneySchema,
    segment: ModalSessionFlightSegmentViewSchema,
    stops: z.number().int().min(0).optional(),
    returnSegment: ModalSessionFlightSegmentViewSchema.optional(),
    returnStops: z.number().int().min(0).optional(),
    /** Pernas da ida / da volta quando o trecho tem CONEXÃO (2+), todas com identidade
     *  completa — o produtor omite a lista inteira se qualquer perna estiver manca.
     *  No nível da OFERTA, fora de `segment`, de propósito: `offerFromView` (genui-kit)
     *  copia `segment` por referência para o wire do card e do web, e este dado é do
     *  agente (lookup Cirium por perna), não do card. */
    legs: z.array(ModalSessionFlightLegRefSchema).min(2).optional(),
    returnLegs: z.array(ModalSessionFlightLegRefSchema).min(2).optional(),
    policy: ModalSessionFlightPolicySchema.optional(),
    /** Sujeito efetivo da política da busca desta oferta. OPCIONAL: sessão legada
     *  sem o registro — e sem `search.policyTravelerId`/dono para derivar — não o
     *  carrega, e o consumidor degrada para a checagem por rota/data. */
    policySubject: ModalSessionFlightPolicySubjectSchema.optional(),
  })
  .strict();
export type ModalSessionFlightOfferView = z.infer<typeof ModalSessionFlightOfferViewSchema>;

export const ModalSessionOfferSchema = z.object({
  offerId: ModalOfferIdSchema,
  title: z.string().min(1).optional(),
  summary: z.string().min(1).optional(),
  total: ModalSessionMoneySchema.optional(),
  durationMinutes: z.number().int().positive().optional(),
  tags: z.array(z.string().min(1)).default([]),
  modalData: ModalPayloadSchema.optional(),
});
export type ModalSessionOffer = z.infer<typeof ModalSessionOfferSchema>;

export const ModalSessionHighlightSchema = z
  .object({
    kind: z.enum(['cheapest', 'fastest', 'recommended']),
    offerId: ModalOfferIdSchema,
    title: z.string().min(1),
    reason: z.string().min(1),
    total: ModalSessionMoneySchema.optional(),
    modalData: ModalPayloadSchema.optional(),
  })
  .strict();
export type ModalSessionHighlight = z.infer<typeof ModalSessionHighlightSchema>;

export const ModalSessionSelectionSchema = z
  .object({
    offerId: ModalOfferIdSchema,
    pricingOptionId: ModalPricingOptionIdSchema.optional(),
    selectedAt: z.iso.datetime().optional(),
    modalData: ModalPayloadSchema.optional(),
  })
  .strict();
export type ModalSessionSelection = z.infer<typeof ModalSessionSelectionSchema>;

export const ModalSessionPricingOptionSchema = z.object({
  pricingOptionId: ModalPricingOptionIdSchema,
  offerId: ModalOfferIdSchema,
  label: z.string().min(1),
  total: ModalSessionMoneySchema,
  refundable: z.boolean().optional(),
  changeable: z.boolean().optional(),
  modalData: ModalPayloadSchema.optional(),
});
export type ModalSessionPricingOption = z.infer<typeof ModalSessionPricingOptionSchema>;

export const ModalSessionPricingMatrixRowSchema = z
  .object({
    rowId: z.string().min(1),
    label: z.string().min(1),
    modalData: ModalPayloadSchema.optional(),
  })
  .strict();
export type ModalSessionPricingMatrixRow = z.infer<typeof ModalSessionPricingMatrixRowSchema>;

export const ModalSessionPricingMatrixColumnSchema = z
  .object({
    columnId: z.string().min(1),
    label: z.string().min(1),
    modalData: ModalPayloadSchema.optional(),
  })
  .strict();
export type ModalSessionPricingMatrixColumn = z.infer<typeof ModalSessionPricingMatrixColumnSchema>;

export const ModalSessionPricingMatrixCellSchema = z
  .object({
    rowId: z.string().min(1),
    columnId: z.string().min(1),
    available: z.boolean(),
    pricingOptionId: ModalPricingOptionIdSchema.optional(),
    total: ModalSessionMoneySchema.optional(),
    modalData: ModalPayloadSchema.optional(),
  })
  .strict();
export type ModalSessionPricingMatrixCell = z.infer<typeof ModalSessionPricingMatrixCellSchema>;

export const ModalSessionPricingMatrixSchema = z
  .object({
    rows: z.array(ModalSessionPricingMatrixRowSchema).default([]),
    columns: z.array(ModalSessionPricingMatrixColumnSchema).default([]),
    cells: z.array(ModalSessionPricingMatrixCellSchema).default([]),
    modalData: ModalPayloadSchema.optional(),
  })
  .strict();
export type ModalSessionPricingMatrix = z.infer<typeof ModalSessionPricingMatrixSchema>;

export const ModalSessionQuoteSchema = z
  .object({
    quoteId: ModalQuoteIdSchema,
    pricingOptionId: ModalPricingOptionIdSchema.optional(),
    status: z.enum(['fresh', 'stale', 'price_changed', 'unavailable', 'expired']).default('fresh'),
    total: ModalSessionMoneySchema,
    createdAt: z.iso.datetime(),
    expiresAt: z.iso.datetime().optional(),
    modalData: ModalPayloadSchema.optional(),
  })
  .strict();
export type ModalSessionQuote = z.infer<typeof ModalSessionQuoteSchema>;

export const ModalSessionCheckoutFieldSchema = z
  .object({
    field: z.string().min(1),
    label: z.string().min(1).optional(),
    required: z.boolean().default(true),
    value: z.unknown().optional(),
  })
  .strict();
export type ModalSessionCheckoutField = z.infer<typeof ModalSessionCheckoutFieldSchema>;

export const ModalSessionCheckoutSchema = z
  .object({
    status: z.enum(['not_ready', 'ready', 'submitted']).default('not_ready'),
    requiredFields: z.array(ModalSessionCheckoutFieldSchema).default([]),
    modalData: ModalPayloadSchema.optional(),
  })
  .strict();
export type ModalSessionCheckout = z.infer<typeof ModalSessionCheckoutSchema>;

export const ModalSessionBookingSchema = z
  .object({
    bookingId: z.string().min(1).optional(),
    status: z.enum([
      'not_requested',
      'booking_requested',
      'pending_approval',
      'confirmed',
      'failed',
    ]),
    locator: z.string().min(1).optional(),
    message: z.string().min(1).optional(),
    // `status: 'failed'` collapses two DIFFERENT facts, and only one of them is
    // a definitive negative:
    //   - the product ANSWERED and refused the booking → no reservation exists;
    //   - our CALL never resolved (timeout/abort/network) → the supplier may
    //     have reserved anyway, and its voucher can still arrive afterwards.
    // `outcome: 'indeterminate'` marks the second case so a consumer that would
    // record a TERMINAL negative — the write-ahead order's `fail` edge, whose
    // FAILED state means "no voucher will ever exist" — holds off instead of
    // closing a booking that may well be alive. Customer-facing handling is
    // unchanged: an indeterminate outcome is still surfaced as an error, because
    // we cannot promise a reservation we did not see. Absent = the product
    // answered (every pre-existing payload, and every non-failed status).
    outcome: z.literal('indeterminate').optional(),
    // Additive rollout evidence: only an exact-v1 receipt match may turn an
    // asynchronous pending acknowledgement into a customer-facing receipt.
    receiptValidation: z.preprocess(
      (value) => (value === 'exact_v1' ? value : undefined),
      z.literal('exact_v1').optional(),
    ),
    modalData: ModalPayloadSchema.optional(),
  })
  .strict();
export type ModalSessionBooking = z.infer<typeof ModalSessionBookingSchema>;

export const ModalSessionCheckoutResponseSchema = z
  .object({
    sessionId: ModalSessionIdSchema,
    checkout: ModalSessionCheckoutSchema,
    // Só com opt-in `includeVoucherCorrelationId` (voo + pricingOptionId): o `correlationId` do
    // voucher legado (= searchKey da opção). Opt-in porque o schema é strict nos consumidores antigos.
    voucherCorrelationId: z.string().min(1).optional(),
  })
  .strict();
export type ModalSessionCheckoutResponse = z.infer<typeof ModalSessionCheckoutResponseSchema>;

export const ModalSessionCheckoutPrepareRequestSchema = z
  .object({
    clientId: z.string().min(1),
    userId: z.string().min(1),
    pricingOptionId: ModalPricingOptionIdSchema.optional(),
    includeVoucherCorrelationId: z.literal(true).optional(),
  })
  .strict();
export type ModalSessionCheckoutPrepareRequest = z.infer<
  typeof ModalSessionCheckoutPrepareRequestSchema
>;

const ModalSessionBookingNamedRefSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1).optional(),
  })
  .strict();

const ModalSessionTravelerCostCenterSchema = z
  .object({
    company: ModalSessionBookingNamedRefSchema,
    costCenter: ModalSessionBookingNamedRefSchema,
    project: ModalSessionBookingNamedRefSchema.nullable().optional(),
    percent: z.number().min(0).max(100),
  })
  .strict();

export const ModalSessionTravelerInputSchema = z
  .object({
    // Optional on the shared wire because hotel guests are name-only. Flight
    // requires this authoritative identity before creating the booking voucher.
    travelerId: z.string().min(1).optional(),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    email: z.string().optional(),
    // Optional on the shared wire: hotel bookings identify the guest by name only
    // (the hotels-api booking person carries no birthdate), while flight supplies
    // it from the traveler profile. An empty string is NOT accepted — omit it when
    // absent rather than sending ''.
    dateOfBirth: z.iso.date().optional(),
    // Object `{ code, name }` — the flight provider's booking voucher schema
    // (`FlightNationalitySchema`) rejects a bare string code. Mirrors the
    // profile source below (`ModalSessionBusinessUserProfileSchema.nationality`).
    nationality: z
      .object({ code: z.string().optional(), name: z.string().optional() })
      .strict()
      .nullable()
      .optional(),
    document: z.string().min(1).optional(),
    documents: z
      .array(
        z
          .object({
            value: z.string(),
            type: z.string(),
            issuingCountry: z.string().optional(),
            expirationDate: z.string().optional(),
          })
          .strict(),
      )
      .optional(),
    phone: z.string().nullable().optional(),
    countryCode: z.string().nullable().optional(),
    gender: z.string().nullable().optional(),
    boardingName: z.string().optional(),
    boardingSurname: z.string().optional(),
    // The corporate allocation belonging to this traveler: every flight
    // passenger, and every hotel ROOM HOLDER (#5522; hotel companions never
    // carry one). Optional because legacy single-traveler flight requests and
    // legacy hotel requests still use `corporateSelection` below. A split (#6697) is
    // checked by the mapper (`costCenterSplitIssue`: up to 10 rows, sum 100%).
    costCenters: z.array(ModalSessionTravelerCostCenterSchema).min(1).optional(),
  })
  .strict();
export type ModalSessionTravelerInput = z.infer<typeof ModalSessionTravelerInputSchema>;

export const ModalSessionBookingRequestSchema = z
  .object({
    clientId: z.string().min(1),
    userId: z.string().min(1),
    pricingOptionId: ModalPricingOptionIdSchema,
    // Optional on the shared wire (flight bookings don't require it); the hotel
    // route enforces its presence and the hotel adapter keys its atomic
    // idempotency claim on it so concurrent retries book the product at most once.
    // Constrained to a Mongo-path-safe charset: the CAS guard interpolates this key
    // into a `bookingIdempotency.<key>` field path, so a `.` or `$` would silently
    // retarget the `$exists` check and break the at-most-once guarantee. Real keys
    // (UUID challengeIds, `sr_`/`order_` ids) already fit this charset.
    idempotencyKey: z
      .string()
      .min(1)
      .regex(/^[A-Za-z0-9_-]+$/)
      .optional(),
    tripId: z.string().min(1).optional(),
    serviceRequestId: z.string().min(1).optional(),
    observation: z.string().max(300).optional(),
    travelers: z.array(ModalSessionTravelerInputSchema).min(1),
    corporateSelection: ModalSessionCorporateSelectionSchema.optional(),
    requester: ModalSessionBookingRequesterSchema.optional(),
    writeAhead: ModalSessionBookingWriteAheadSchema.optional(),
  })
  .strict();
export type ModalSessionBookingRequest = z.infer<typeof ModalSessionBookingRequestSchema>;

export const ModalSessionFlightTravelerProfileRequestSchema = z
  .object({
    clientId: z.string().min(1),
    userId: z.string().min(1),
  })
  .strict();
export type ModalSessionFlightTravelerProfileRequest = z.infer<
  typeof ModalSessionFlightTravelerProfileRequestSchema
>;

const ModalSessionFlightTravelerProfileSchema = z
  .object({
    travelerId: z.string().min(1),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    dateOfBirth: z.iso.date().optional(),
  })
  .strict();

export const ModalSessionFlightTravelerProfileResponseSchema = z
  .object({
    profile: ModalSessionFlightTravelerProfileSchema.nullable(),
  })
  .strict();
export type ModalSessionFlightTravelerProfileResponse = z.infer<
  typeof ModalSessionFlightTravelerProfileResponseSchema
>;

export const ModalSessionFlightTravelerSearchRequestSchema = z
  .object({
    clientId: z.string().min(1),
    userId: z.string().min(1),
    term: z.string().trim().min(1).max(120),
  })
  .strict();
export type ModalSessionFlightTravelerSearchRequest = z.infer<
  typeof ModalSessionFlightTravelerSearchRequestSchema
>;

const ModalSessionFlightTravelerSearchResultSchema = z
  .object({
    travelerId: z.string().min(1),
    name: z.string().min(1),
  })
  .strict();

export const ModalSessionFlightTravelerSearchResponseSchema = z
  .object({
    travelers: z.array(ModalSessionFlightTravelerSearchResultSchema).max(20),
    truncated: z.boolean(),
  })
  .strict();
export type ModalSessionFlightTravelerSearchResponse = z.infer<
  typeof ModalSessionFlightTravelerSearchResponseSchema
>;

export const ModalSessionFlightBusinessApprovalRequestSchema = z
  .object({
    clientId: z.string().min(1),
    userId: z.string().min(1),
  })
  .strict();
export type ModalSessionFlightBusinessApprovalRequest = z.infer<
  typeof ModalSessionFlightBusinessApprovalRequestSchema
>;

const ModalSessionBusinessOptionSchema = z
  .object({
    id: z.string().min(1),
    name: z.string(),
    // Código externo do cadastro do cliente (só centros de custo o trazem). É o
    // que identifica o item quando o `name` vem vazio — sem ele a opção renderiza
    // em branco.
    code: z.string().optional(),
  })
  .strict();

const ModalSessionBusinessUserProfileSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().optional(),
    email: z.string().optional(),
    phone: z.string().optional(),
    countryCode: z.string().optional(),
    gender: z.string().optional(),
    birthdate: z.string().optional(),
    nationality: z
      .object({
        code: z.string().optional(),
        name: z.string().optional(),
      })
      .strict()
      .optional(),
    maskedCpf: z.string().optional(),
    maskedPassport: z.string().optional(),
    maskedPassportCountry: z.string().optional(),
    maskedPassportValidate: z.string().optional(),
    maskedDni: z.string().optional(),
    boardingName: z
      .object({
        first: z.string().optional(),
        last: z.string().optional(),
      })
      .strict()
      .optional(),
  })
  .strict();

const ModalSessionCostCenterDefaultSchema = z
  .object({
    company: ModalSessionBusinessOptionSchema,
    // Nullable: empresa default SEM centro de custo default é um estado válido do
    // backend, e descartar o par inteiro tirava a pré-seleção da empresa também.
    costCenter: ModalSessionBusinessOptionSchema.nullable(),
  })
  .strict();

const ModalSessionCompanyWithCostCentersSchema = z
  .object({
    id: z.string().min(1),
    name: z.string(),
    // `true` ⇒ catálogo embutido NÃO é exaustivo (a empresa libera todos os
    // centros de custo); a lista vazia significa "busque no servidor". Default
    // `false` mantém payloads antigos/fixtures válidos.
    allCostCenters: z.boolean().default(false),
    costCenters: z.array(ModalSessionBusinessOptionSchema).default([]),
  })
  .strict();

export const ModalSessionFlightBusinessApprovalResponseSchema = z
  .object({
    approval: z
      .object({
        user: ModalSessionBusinessUserProfileSchema.nullable(),
        default: ModalSessionCostCenterDefaultSchema.nullable(),
        companies: z.array(ModalSessionCompanyWithCostCentersSchema).default([]),
      })
      .strict(),
    gender: z.string().min(1).optional(),
  })
  .strict();
export type ModalSessionFlightBusinessApprovalResponse = z.infer<
  typeof ModalSessionFlightBusinessApprovalResponseSchema
>;

export const ModalSessionFlightPreferencesRequestSchema = z
  .object({
    clientId: z.string().min(1),
    userId: z.string().min(1),
  })
  .strict();
export type ModalSessionFlightPreferencesRequest = z.infer<
  typeof ModalSessionFlightPreferencesRequestSchema
>;

export const ModalSessionFlightPreferencesResponseSchema = z
  .object({
    preferences: TravelerTravelPreferencesSchema,
  })
  .strict();
export type ModalSessionFlightPreferencesResponse = z.infer<
  typeof ModalSessionFlightPreferencesResponseSchema
>;

export const ModalSessionFlightVoucherLookupRequestSchema = z
  .object({
    clientId: z.string().min(1),
    userId: z.string().min(1),
    locator: z.string().min(1),
  })
  .strict();
export type ModalSessionFlightVoucherLookupRequest = z.infer<
  typeof ModalSessionFlightVoucherLookupRequestSchema
>;

export const ModalSessionFlightVoucherSummarySchema = z
  .object({
    locator: z.string().min(1),
    userId: z.string().min(1),
    product: z.string().min(1),
    status: z.string().min(1),
    travelerName: z.string(),
    supplier: z.string().min(1).optional(),
    amount: z.number().optional(),
    currency: z.string().min(3).max(3).optional(),
    reservationNumber: z.string().min(1).optional(),
  })
  .strict();
export type ModalSessionFlightVoucherSummary = z.infer<
  typeof ModalSessionFlightVoucherSummarySchema
>;

export const ModalSessionFlightVoucherLookupResponseSchema = z
  .object({
    found: z.boolean(),
    vouchers: z.array(ModalSessionFlightVoucherSummarySchema),
  })
  .strict();
export type ModalSessionFlightVoucherLookupResponse = z.infer<
  typeof ModalSessionFlightVoucherLookupResponseSchema
>;

/**
 * Dica de reuso: `{ sessionId, offerId }` de UMA oferta de OUTRA sessão do mesmo
 * dono, da MESMA busca (o BFF decodifica a ref opaca do card). O adapter lê o
 * `searchKey` privado dessa oferta e busca no cache do provider em vez de abrir
 * busca nova no fornecedor. É dica, não ordem: sessão fonte expirada, params
 * diferentes, oferta ausente ou cache vazio caem na busca de hoje. Só ids
 * públicos viajam — o `searchKey` nunca sai do estado privado.
 */
export const ModalSessionFlightSearchReuseSchema = z
  .object({
    sessionId: ModalSessionIdSchema,
    offerId: ModalOfferIdSchema,
  })
  .strict();
export type ModalSessionFlightSearchReuse = z.infer<typeof ModalSessionFlightSearchReuseSchema>;

/**
 * Cortes de voo aplicados NO SERVIDOR — os eixos do painel de filtro que o
 * provider conhece, reusando o `searchKey` da busca já feita (busca nova custa
 * 12–40s; a mesma busca filtrada responde em 200–800ms).
 *
 * Janela de CHEGADA fica de fora de propósito: o fio do provider não tem o
 * campo, e um corte que o servidor não sabe aplicar mentiria na contagem —
 * ela segue cortando no cliente, sobre a página. Aeroporto de conexão entrou
 * com `avoidAirports` (P4).
 */
export const ModalSessionFlightCutsSchema = z
  .object({
    // O teto da UNIÃO das opções de paradas do painel (#2111). "Acima de 2
    // paradas" fica de fora de propósito: o provider só filtra tetos
    // (`TakeInfoStops`), então esse corte é do cliente, sobre a página.
    stops: z.enum(['nonstop', 'up_to_1', 'up_to_2']).optional(),
    /** IATA de companhia. Vazio = todas. */
    airlines: z.array(z.string().min(2).max(3)).max(30).optional(),
    /** Piso da régua de preço (#2110). Só viaja acompanhado de `maxPrice` — o
     *  `priceRange` do provider é uma faixa fechada. */
    minPrice: z.number().positive().optional(),
    maxPrice: z.number().positive().optional(),
    /** Número do voo em DÍGITOS ("2084"). A parte de companhia do código fica
     *  no corte do cliente; letra ou espaço falha aqui, não no provider —
     *  mesma régua do HH:mm de `departureWindows`. */
    number: z
      .string()
      .regex(/^\d{1,6}$/)
      .optional(),
    /** Faixas de partida `HH:mm`, unidas por OU (Tarde E Noite juntas). */
    departureWindows: z
      .array(
        z
          .object({
            // Hora de relógio, não dois pares de dígitos: o valor atravessa até o
            // `timeRanges` do provider, e `99:99` chegaria lá em vez de falhar aqui.
            start: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
            end: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
          })
          .strict(),
      )
      .max(8)
      .optional(),
    withinPolicyOnly: z.boolean().optional(),
    /** IATAs de aeroporto de CONEXÃO a excluir — `avoidAirports` do provider
     *  (flight-business `feat/flight-search-avoid-airports`): o servidor corta o
     *  CONJUNTO sobre `filter.connections`; direto nunca cai. */
    avoidAirports: z
      .array(z.string().regex(/^[A-Za-z]{3}$/))
      .max(10)
      .optional(),
    /** IATAs de aeroporto de CONEXÃO exigidos — pelo menos um (`viaAirports` do
     *  provider, `$in` sobre `filter.connections`, flight-business 6af63ea5).
     *  Direto nunca satisfaz. */
    viaAirports: z
      .array(z.string().regex(/^[A-Za-z]{3}$/))
      .max(10)
      .optional(),
  })
  .strict();
export type ModalSessionFlightCuts = z.infer<typeof ModalSessionFlightCutsSchema>;

/**
 * Ordenação da busca — vocabulário NOSSO, que o provider traduz (`OrderSearch`).
 * Vive fora de `cuts` porque não é corte: não muda o conjunto, muda a ordem, e as
 * sondas de faceta precisam forçar a própria ordem sem mexer nos cortes.
 *
 * Importa que ela viaje: ordenar no cliente ordena a PÁGINA, e a página é um
 * subconjunto — "mais barato" sobre 100 de 544 não é o mais barato da busca.
 */
export const ModalSessionFlightOrderSchema = z.enum([
  'recommended',
  'cheapest',
  'fastest',
  'earliest',
  'earlier_arrival',
]);
export type ModalSessionFlightOrder = z.infer<typeof ModalSessionFlightOrderSchema>;

/** Teto de uma página do refine/return-search de voo. O explorer pede 100 (a
 *  página que o refine já servia inteira); o teto impede um cliente de virar
 *  varredura. Espelho do MODAL_SESSION_HOTEL_PAGE_LIMIT_MAX — tetos separados
 *  porque os inventários e páginas dos dois modais são diferentes. */
export const MODAL_SESSION_FLIGHT_PAGE_LIMIT_MAX = 100;

export const ModalSessionFlightPageSchema = z
  .object({
    // 1-based: mesmo contrato do provider (pagination.number do voll-apollo).
    number: z.number().int().positive(),
    limit: z.number().int().positive().max(MODAL_SESSION_FLIGHT_PAGE_LIMIT_MAX),
  })
  .strict();
export type ModalSessionFlightPage = z.infer<typeof ModalSessionFlightPageSchema>;

/** O que o scroll infinito precisa saber da página que chegou. `total` é a
 *  contagem do conjunto FILTRADO inteiro (searchSize do provider) — no refine
 *  ela também viaja em `facets.total`; aqui é o caminho do return-search, que
 *  não tem facetas. */
export const ModalSessionFlightPageInfoSchema = z
  .object({
    number: z.number().int().positive(),
    hasNext: z.boolean(),
    total: z.number().int().nonnegative().optional(),
  })
  .strict();
export type ModalSessionFlightPageInfo = z.infer<typeof ModalSessionFlightPageInfoSchema>;

export const ModalSessionFlightRefineRequestSchema = z
  .object({
    clientId: z.string().min(1),
    userId: z.string().min(1),
    cuts: ModalSessionFlightCutsSchema,
    order: ModalSessionFlightOrderSchema.optional(),
    // Ausente ⇒ página 1 com o limit de hoje (comportamento intacto).
    page: ModalSessionFlightPageSchema.optional(),
  })
  .strict();
export type ModalSessionFlightRefineRequest = z.infer<typeof ModalSessionFlightRefineRequestSchema>;

/** Quantos voos uma opção deixa e a partir de quanto — o par que vai na ponta de
 *  toda linha do painel, apurado pelo PROVIDER sobre o conjunto inteiro. */
export const ModalSessionOptionFacetSchema = z
  .object({
    count: z.number().int().nonnegative(),
    from: z.number().positive().optional(),
  })
  .strict();
export type ModalSessionOptionFacet = z.infer<typeof ModalSessionOptionFacetSchema>;

/**
 * As facetas do filtro: a contagem de cada opção com os OUTROS eixos ativos e o
 * próprio eixo solto (padrão do protótipo), mais as companhias do conjunto e a
 * faixa de preço da busca inteira (que não anda a cada corte).
 */
export const ModalSessionFlightFacetsSchema = z
  .object({
    total: z.number().int().nonnegative(),
    priceRange: z.object({ min: z.number(), max: z.number() }).strict().optional(),
    // Chave OPCIONAL e objeto FROUXO nos dois eixos, e é o mesmo cuidado do resto do
    // fio: `z.record` com chave enum é exaustivo em zod v4 — ele rejeita o payload
    // INTEIRO quando falta uma chave e também quando aparece uma chave nova. Isso
    // faria um produtor à frente do cliente (um 5º período, um `up_to_2`) derrubar
    // TODAS as facetas em vez de degradar a linha, e deixava morto o fallback de
    // faceta ausente que o painel já tem (`{n: 0, from: null}`).
    stops: z.looseObject({
      any: ModalSessionOptionFacetSchema.optional(),
      nonstop: ModalSessionOptionFacetSchema.optional(),
      up_to_1: ModalSessionOptionFacetSchema.optional(),
    }),
    departurePeriods: z.looseObject({
      dawn: ModalSessionOptionFacetSchema.optional(),
      morning: ModalSessionOptionFacetSchema.optional(),
      afternoon: ModalSessionOptionFacetSchema.optional(),
      evening: ModalSessionOptionFacetSchema.optional(),
    }),
    withinPolicy: ModalSessionOptionFacetSchema,
    airlines: z.array(
      z
        .object({
          code: z.string().min(2).max(3),
          name: z.string().min(1),
          facet: ModalSessionOptionFacetSchema,
        })
        .strict(),
    ),
  })
  .strict();
export type ModalSessionFlightFacets = z.infer<typeof ModalSessionFlightFacetsSchema>;

const HotelPersonsInRoomSchema = z.enum(['SINGLE', 'DOUBLE', 'TRIPLE', 'QUADRUPLE']);

/**
 * Os eixos de corte que a leitura PAGINADA da busca de hotel honra — exatamente os
 * que o fornecedor aplica no cache da `searchKey` (`getCriteria`, hotels-api). Por
 * isso são um schema só: a busca inicial e o refino paginado precisam falar a
 * mesma língua, senão a página 2 sai de um conjunto diferente da página 1.
 *
 * Ficam DE FORA por contrato do fornecedor, não por esquecimento:
 * `onlyBreakfast`/`onlyRefundable` (aceitos pelo schema deles e nunca lidos em
 * nenhum caminho) e comodidade (sem critério no cache). O painel filtra esses
 * sobre o que já carregou.
 */
export const ModalSessionHotelCutsSchema = z.object({
  category: z.array(z.number().int().positive()).optional(),
  rating: z
    .array(
      z
        .object({
          from: z.number().nonnegative().optional(),
          to: z.number().nonnegative().optional(),
        })
        .strict(),
    )
    .optional(),
  minPrice: z.number().nonnegative().optional(),
  maxPrice: z.number().nonnegative().optional(),
  onlyRecommended: z.boolean().optional(),
  onlyPolicyWithIn: z.boolean().optional(),
  restrictRadiusTo: z.number().positive().optional(),
  onlyAgreedRate: z.boolean().optional(),
});
export type ModalSessionHotelCuts = z.infer<typeof ModalSessionHotelCutsSchema>;

export const ModalSessionHotelSearchRequestSchema = z
  .object({
    clientId: z.string().min(1),
    userId: z.string().min(1),
    search: z
      .object({
        checkin: z.iso.date(),
        checkout: z.iso.date(),
        rooms: z.number().int().positive().default(1),
        personsInRoom: HotelPersonsInRoomSchema.default('SINGLE'),
        travelerId: z.string().min(1).optional(),
        place: z.record(z.string(), z.unknown()).optional(),
        destinationId: z.number().int().positive().optional(),
        // Busca ANCORADA num hotel (id estável do provider): o trilho do hotel
        // favorito e a re-estadia da Página do Hotel pedem os quartos de UM
        // hotel em outra janela de datas, e o fornecedor não tem "detalhe por
        // id com preço" — preço só nasce de busca. O adapter mapeia para
        // `hotelsId: [id]` do SearchHotel e PÓS-FILTRA os itens (fornecedor
        // que ignorar o filtro não vaza a cidade inteira).
        hotelId: z.string().min(1).optional(),
        order: z.string().min(1).optional(),
        onlyBreakfast: z.boolean().optional(),
        onlyRefundable: z.boolean().optional(),
        showResumeHotels: z.boolean().optional(),
        // Aceito por compatibilidade de fio (o BFF repassa quando um chamador
        // manda; nenhum manda hoje) e IGNORADO pelo adapter desde a conclusão
        // do inventário em segundo plano (spec 2026-09-21, #3449): a sessão
        // recebe a busca inteira sem teto por chamador. Remoção do campo é
        // follow-up (toca BFF e web-chat).
        offerCap: z.number().int().min(1).max(50).optional(),
      })
      .extend(ModalSessionHotelCutsSchema.shape)
      .strict(),
  })
  .strict();
export type ModalSessionHotelSearchRequest = z.infer<typeof ModalSessionHotelSearchRequestSchema>;

/** Teto de uma página do refino de hotel. O painel pede 20; o teto existe para
 *  um cliente não transformar "carregar mais" numa varredura das 300 do cache. */
export const MODAL_SESSION_HOTEL_PAGE_LIMIT_MAX = 50;

/** Raio padrão (km) que TODA busca de hotel manda como `restrictRadiusTo`: sem
 *  ele o fornecedor devolvia hotéis espalhados pelo sudeste inteiro numa busca
 *  por Belo Horizonte; 20 km cobre a região metropolitana da cidade buscada sem
 *  excluí-la. Fonte ÚNICA (#2302): o BFF do painel e o provider do agents
 *  derivam DAQUI — com o número pinado independente nos dois runtimes, nenhum
 *  teste trava a divergência (mutar um lado deixa o teste do outro verde), e
 *  busca e provider passariam a discordar em silêncio sobre a área coberta.
 *  Fixo por ora; ainda não é filtro exposto ao viajante. */
export const HOTEL_SEARCH_RADIUS_KM = 20;

export const ModalSessionHotelPageSchema = z
  .object({
    // 1-based: é o que a hotels-api devolve em `pageInfo.current` e o que ela
    // trata como primeira página (`offset <= 0` vira 1 lá dentro).
    number: z.number().int().positive(),
    limit: z.number().int().positive().max(MODAL_SESSION_HOTEL_PAGE_LIMIT_MAX),
  })
  .strict();
export type ModalSessionHotelPage = z.infer<typeof ModalSessionHotelPageSchema>;

/**
 * Refino de hotel: os cortes do painel aplicados NO FORNECEDOR, sobre a MESMA
 * `searchKey` da busca desta sessão — nunca uma busca nova (aquela reprocessa o
 * cache inteiro). É também o ÚNICO caminho paginado: a busca inicial roda com
 * `showResumeHotels`, e nesse modo a hotels-api ignora a paginação e devolve a
 * lista resumida (página 1, até 30). O refino lê a lista completa.
 */
export const ModalSessionHotelRefineRequestSchema = z
  .object({
    clientId: z.string().min(1),
    userId: z.string().min(1),
    cuts: ModalSessionHotelCutsSchema.optional(),
    order: z.string().min(1).optional(),
    page: ModalSessionHotelPageSchema,
  })
  .strict();
export type ModalSessionHotelRefineRequest = z.infer<typeof ModalSessionHotelRefineRequestSchema>;

export const ModalSessionBookingResponseSchema = z
  .object({
    sessionId: ModalSessionIdSchema,
    booking: ModalSessionBookingSchema,
    quote: ModalSessionQuoteSchema.optional(),
    correlation: ModalSessionCorrelationSchema.optional(),
  })
  .strict();
export type ModalSessionBookingResponse = z.infer<typeof ModalSessionBookingResponseSchema>;

export const CreateModalSessionRequestSchema = z
  .object({
    orgId: z.string().min(1),
    userId: z.string().min(1).optional(),
    conversationId: z.string().min(1).optional(),
    tripId: z.string().min(1).optional(),
    serviceRequestId: z.string().min(1).optional(),
    modal: ModalSessionModalSchema,
    search: ModalPayloadSchema.optional(),
    expiresAt: z.iso.datetime().optional(),
    itinerary: ModalSessionItineraryLineageSchema.optional(),
  })
  .strict();
export type CreateModalSessionRequest = z.infer<typeof CreateModalSessionRequestSchema>;

export const ModalSessionViewSchema = z.object({
  sessionId: ModalSessionIdSchema,
  owner: ModalSessionOwnerSchema,
  correlation: ModalSessionCorrelationSchema.default({}),
  modal: ModalSessionModalSchema,
  status: ModalSessionStatusSchema,
  search: ModalPayloadSchema.nullable().default(null),
  offers: z.array(ModalSessionOfferSchema).default([]),
  highlights: z.array(ModalSessionHighlightSchema).default([]),
  selection: ModalSessionSelectionSchema.nullable().default(null),
  pricingOptions: z.array(ModalSessionPricingOptionSchema).default([]),
  quote: ModalSessionQuoteSchema.nullable().default(null),
  checkout: ModalSessionCheckoutSchema.nullable().default(null),
  booking: ModalSessionBookingSchema.nullable().default(null),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
});
export type ModalSessionView = z.infer<typeof ModalSessionViewSchema>;

export const CreateModalSessionResponseSchema = z
  .object({
    sessionId: ModalSessionIdSchema,
    session: ModalSessionViewSchema,
  })
  .strict();
export type CreateModalSessionResponse = z.infer<typeof CreateModalSessionResponseSchema>;

export const ModalSessionOffersResponseSchema = z
  .object({
    sessionId: ModalSessionIdSchema,
    offers: z.array(ModalSessionOfferSchema).default([]),
    highlights: z.array(ModalSessionHighlightSchema).default([]),
  })
  .strict();
export type ModalSessionOffersResponse = z.infer<typeof ModalSessionOffersResponseSchema>;

export const ModalSessionSearchResponseSchema = z
  .object({
    sessionId: ModalSessionIdSchema,
    session: ModalSessionViewSchema,
    offers: z.array(ModalSessionOfferSchema).default([]),
    highlights: z.array(ModalSessionHighlightSchema).default([]),
  })
  .strict();
export type ModalSessionSearchResponse = z.infer<typeof ModalSessionSearchResponseSchema>;

export const ModalSessionFlightSearchResponseSchema = ModalSessionSearchResponseSchema.extend({
  offerViews: z.array(ModalSessionFlightOfferViewSchema).default([]),
}).strict();
export type ModalSessionFlightSearchResponse = z.infer<
  typeof ModalSessionFlightSearchResponseSchema
>;

/** Resposta do refine: a MESMA vista da busca (a página filtrada) + as facetas
 *  que o painel precisa pra desenhar as opções que ela não contém. */
export const ModalSessionFlightRefineResponseSchema = ModalSessionFlightSearchResponseSchema.extend(
  {
    facets: ModalSessionFlightFacetsSchema,
    page: ModalSessionFlightPageInfoSchema.optional(),
  },
).strict();
export type ModalSessionFlightRefineResponse = z.infer<
  typeof ModalSessionFlightRefineResponseSchema
>;

/** O que o scroll infinito precisa saber da página que acabou de chegar.
 *  `total` é a contagem do conjunto FILTRADO inteiro (`searchHotelMetadata`), a
 *  única fonte honesta do "X de Y" — `pageInfo.size` do fornecedor é o tamanho
 *  da página, não o total. Opcional: a contagem é uma leitura à parte e a
 *  página não morre se ela falhar. */
export const ModalSessionHotelPageInfoSchema = z
  .object({
    number: z.number().int().positive(),
    hasNext: z.boolean(),
    total: z.number().int().nonnegative().optional(),
  })
  .strict();
export type ModalSessionHotelPageInfo = z.infer<typeof ModalSessionHotelPageInfoSchema>;

/** Resposta do refino de hotel: a mesma vista da busca (a página filtrada) mais
 *  onde ela cai no conjunto. Sem facetas — o painel de hotel desenha suas opções
 *  a partir das ofertas, não de contagens por eixo. */
export const ModalSessionHotelRefineResponseSchema = ModalSessionSearchResponseSchema.extend({
  page: ModalSessionHotelPageInfoSchema,
}).strict();
export type ModalSessionHotelRefineResponse = z.infer<typeof ModalSessionHotelRefineResponseSchema>;

export const ModalSessionReturnSearchResponseSchema = ModalSessionFlightSearchResponseSchema.extend(
  { page: ModalSessionFlightPageInfoSchema.optional() },
)
  .strict()
  .describe('ModalSessionReturnSearchResponse');
export type ModalSessionReturnSearchResponse = z.infer<
  typeof ModalSessionReturnSearchResponseSchema
>;

export const ModalSessionOfferDetailsResponseSchema = z
  .object({
    sessionId: ModalSessionIdSchema,
    offer: ModalSessionOfferSchema,
  })
  .strict();
export type ModalSessionOfferDetailsResponse = z.infer<
  typeof ModalSessionOfferDetailsResponseSchema
>;

export const ModalSessionHotelOfferHandleResponseSchema = z
  .object({ sessionId: ModalSessionIdSchema })
  .strict();
export type ModalSessionHotelOfferHandleResponse = z.infer<
  typeof ModalSessionHotelOfferHandleResponseSchema
>;

/** Resposta da rota offer-view do modal-session: a vista pública de UMA oferta,
 *  combo-aware — um ref de VOLTA devolve a vista do PAR (ida + volta + total do
 *  par) reconstruída do estado privado da sessão. Sem echo da sessão. */
export const ModalSessionFlightOfferViewResponseSchema = z
  .object({
    sessionId: ModalSessionIdSchema,
    offerView: ModalSessionFlightOfferViewSchema,
  })
  .strict();
export type ModalSessionFlightOfferViewResponse = z.infer<
  typeof ModalSessionFlightOfferViewResponseSchema
>;

export const ModalSessionHotelPricingRequestSchema = z
  .object({
    clientId: z.string().min(1),
    userId: z.string().min(1),
    roomIndex: z.number().int().positive().max(99),
  })
  .strict();
export type ModalSessionHotelPricingRequest = z.infer<typeof ModalSessionHotelPricingRequestSchema>;

export const ModalSessionPricingResponseSchema = z
  .object({
    sessionId: ModalSessionIdSchema,
    offerId: ModalOfferIdSchema,
    pricingOptions: z.array(ModalSessionPricingOptionSchema).default([]),
    pricingMatrix: ModalSessionPricingMatrixSchema,
  })
  .strict();
export type ModalSessionPricingResponse = z.infer<typeof ModalSessionPricingResponseSchema>;

export const ModalSessionSelectionResponseSchema = z
  .object({
    sessionId: ModalSessionIdSchema,
    selection: ModalSessionSelectionSchema,
  })
  .strict();
export type ModalSessionSelectionResponse = z.infer<typeof ModalSessionSelectionResponseSchema>;

export const ModalSessionQuoteAcceptanceRequestSchema = z
  .object({
    quoteId: ModalQuoteIdSchema,
    clientId: z.string().min(1),
    userId: z.string().min(1),
  })
  .strict();
export type ModalSessionQuoteAcceptanceRequest = z.infer<
  typeof ModalSessionQuoteAcceptanceRequestSchema
>;

export const ModalSessionQuoteResponseSchema = z
  .object({
    sessionId: ModalSessionIdSchema,
    quote: ModalSessionQuoteSchema,
  })
  .strict();
export type ModalSessionQuoteResponse = z.infer<typeof ModalSessionQuoteResponseSchema>;
